import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet } from 'react-router'

import { Avatar } from '@/components/Chips'
import { Icon, type IconName } from '@/components/Icon'
import { SpacesTree } from '@/app/layout/SpacesTree'
import { useLogout, useSession } from '@/features/auth/useSession'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { SearchBox } from '@/features/search/SearchBox'
import i18n, { applyLocale, type Locale } from '@/lib/i18n'

/**
 * The shell.
 *
 * Laid out the way the work tools people already use are laid out: a persistent
 * left rail carrying search, the handful of places you go every day, and the
 * hierarchy of everything else — so reaching a board is one click from wherever
 * you are, rather than a trip through a picker.
 *
 * The palette stays AWJ's own. Borrowing another product's information
 * architecture is sensible; borrowing its brand is not.
 *
 * Each entry names its audience. A guest is a client contact invited to one
 * project: what the group intends to take on next quarter, who works here, and
 * how the rest of the portfolio is doing are not part of that invitation, so
 * those links are not rendered for them at all rather than rendered and then
 * refused. Every one of these endpoints enforces the same rule server-side;
 * this is the courtesy layer, not the security one.
 */
const NAV: { to: string; key: string; icon: IconName; audience: 'everyone' | 'internal' | 'directory' }[] = [
  { to: '/brief', key: 'nav.brief', icon: 'sparkle', audience: 'internal' },
  { to: '/work', key: 'nav.work', icon: 'inbox', audience: 'everyone' },
  // A guest's landing page, and the fastest way to the board for everyone
  // else — it falls back to the first project in reach when no key is given.
  { to: '/boards', key: 'nav.boards', icon: 'board', audience: 'everyone' },
  { to: '/portfolio', key: 'nav.portfolio', icon: 'chart', audience: 'internal' },
  { to: '/planning', key: 'nav.planning', icon: 'calendar', audience: 'internal' },
  { to: '/support', key: 'nav.support', icon: 'ticket', audience: 'everyone' },
  { to: '/clients', key: 'nav.clients', icon: 'people', audience: 'internal' },
  { to: '/organization', key: 'nav.organization', icon: 'home', audience: 'directory' },
  { to: '/access-review', key: 'nav.accessReview', icon: 'people', audience: 'directory' },
  { to: '/settings', key: 'nav.settings', icon: 'board', audience: 'internal' },
]

export function AppShell() {
  const { t } = useTranslation()
  const session = useSession()
  const logoutMutation = useLogout()
  const [collapsed, setCollapsed] = useState(false)

  const user = session.data?.user
  const isGuest = user?.kind === 'guest'
  const seesDirectory = user?.seesDirectory ?? false

  const nav = NAV.filter((item) => {
    if (item.audience === 'everyone') return true
    if (item.audience === 'directory') return seesDirectory
    return !isGuest
  })

  function switchLocale(locale: Locale) {
    void i18n.changeLanguage(locale)
    applyLocale(locale)
  }

  const current = i18n.language as Locale

  return (
    <div
      className="min-h-dvh lg:grid"
      style={{ gridTemplateColumns: collapsed ? '60px minmax(0,1fr)' : '264px minmax(0,1fr)' }}
    >
      <aside className="border-line bg-surface flex flex-col border-e lg:sticky lg:top-0 lg:h-dvh">
        <div className="flex items-center gap-2.5 px-4 py-3.5">
          <img src="/awj-systems-logo-v.svg" alt="" className="h-6 w-auto shrink-0" />
          {collapsed ? null : (
            <div className="min-w-0 leading-tight">
              <div className="text-ink-900 truncate text-[13px] font-semibold">{t('app.name')}</div>
              <div className="text-ink-500 truncate text-[11px]">
                {session.data?.organization.name ?? ''}
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((state) => !state)}
            title={collapsed ? t('nav.expand') : t('nav.collapse')}
            className="text-ink-500 hover:bg-surface-2 hover:text-ink-900 ms-auto hidden rounded p-1 lg:block"
          >
            <Icon
              name="chevron"
              className={`size-3.5 rtl:-scale-x-100 ${collapsed ? '' : 'rotate-180'}`}
            />
          </button>
        </div>

        {collapsed ? null : <SearchBox />}

        <nav className="flex-1 overflow-y-auto pb-2">
          <ul className="flex flex-col gap-0.5 px-2">
            {nav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  title={collapsed ? t(item.key) : undefined}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                      isActive
                        ? 'bg-brand-50 text-brand-700 font-medium'
                        : 'text-ink-700 hover:bg-surface-2',
                    ].join(' ')
                  }
                >
                  <Icon name={item.icon} className="size-4" />
                  {collapsed ? null : <span className="truncate">{t(item.key)}</span>}
                </NavLink>
              </li>
            ))}
          </ul>

          {collapsed ? null : <SpacesTree />}
        </nav>

        {session.data === undefined || collapsed ? null : (
          <div className="border-line flex items-center gap-2 border-t px-4 py-2.5">
            <Avatar name={session.data.user.name} color={session.data.user.color} />
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-ink-900 truncate text-[12px] font-medium">
                {session.data.user.name}
              </div>
              <button
                type="button"
                onClick={() => logoutMutation.mutate()}
                className="text-ink-500 hover:text-ink-900 text-[11px] underline"
              >
                {t('auth.signOut')}
              </button>
            </div>
          </div>
        )}

        <div className="border-line flex items-center gap-1 border-t px-3 py-2">
          {(['en', 'ar'] as const).map((locale) => (
            <button
              key={locale}
              type="button"
              onClick={() => switchLocale(locale)}
              aria-pressed={current === locale}
              className={[
                'rounded px-2 py-0.5 font-mono text-[11px] uppercase',
                current === locale ? 'bg-brand-600 text-white' : 'text-ink-500 hover:bg-surface-2',
              ].join(' ')}
            >
              {locale}
            </button>
          ))}
        </div>
      </aside>

      <main className="bg-ground min-w-0 px-6 pb-6 lg:px-8">
        <div className="flex items-center justify-end py-2.5">
          <NotificationBell />
        </div>

        <Outlet />
      </main>
    </div>
  )
}
